import type {
  AnalysisConfigOutput,
  GroupAnalysisSpanData,
} from '@/generated/types/navigator/types.gen';
import { useClientTerminology } from '@/views/list/NetworkTerms';
import { capitalize } from '@/helpers/stringConversion';
import { FOOT_TO_METER, useFormatDistance } from '@/helpers/distanceMetric';

export type Level3ItemProps = {
  a: GroupAnalysisSpanData;
};

export const OUTSIDE_ROW = 'Outside right of way';

export const useMinDistToLineFormatter = (
  minDistSpanInFt?: number | null,
  bufferInFt?: number,
  metricUnit?: AnalysisConfigOutput['metric']
): string => {
  let minDistToLine: number | undefined;
  let maxDistToLine =
    metricUnit === 'm' && bufferInFt ? bufferInFt * FOOT_TO_METER : bufferInFt;

  if (
    minDistSpanInFt !== undefined &&
    minDistSpanInFt !== null &&
    minDistSpanInFt >= 0
  ) {
    const roundedMinDistSpan =
      metricUnit === 'ft'
        ? Math.round(minDistSpanInFt)
        : Math.round(minDistSpanInFt * FOOT_TO_METER);

    minDistToLine = roundedMinDistSpan === 0 ? 0 : roundedMinDistSpan - 1;
    maxDistToLine = roundedMinDistSpan + 1;
  }

  const useFormatMinDistToLine = (metric?: number, showUnits = false) => {
    return useFormatDistance(
      metric,
      metricUnit === 'ft' ? 'foot' : 'meter',
      0,
      0,
      false,
      showUnits,
      'short'
    );
  };

  const formattedMinDistToLine = useFormatMinDistToLine(minDistToLine);
  const formattedMaxDistToLine = useFormatMinDistToLine(maxDistToLine, true);

  if (maxDistToLine === undefined) {
    return 'N/A';
  }

  if (minDistToLine !== undefined) {
    return `${formattedMinDistToLine} - ${formattedMaxDistToLine}`;
  }
  return `> ${formattedMaxDistToLine}`;
};

export const formatPhasing = (phasing: string): string => {
  if (!phasing) {
    return phasing;
  }

  return capitalize(phasing).replace('-', ' ');
};

export const useVoltageFormatter = (): ((
  voltage: number | undefined
) => string) => {
  const intl = useClientTerminology();

  return (voltage: number | undefined): string => {
    if (voltage === undefined || voltage === -1) return 'N/A';

    return `${intl.formatNumber(voltage, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })} kV`;
  };
};
